// Dimension preferences (localStorage-backed).
//
// mode='default'  → DimensionValue uses the algorithmic defaults per value.
// mode='custom'   → DimensionValue still starts from algorithmic defaults but
//                   user-selected unit/decimals are remembered across the
//                   session for matching dimension kinds. Mode is reserved
//                   for future preference UI; the per-value dropdowns work
//                   in both modes today.
import { useCallback, useEffect, useState } from 'react';

const KEY = 'tk_dimension_prefs';

export type DimensionPreferenceMode = 'default' | 'custom';

export interface DimensionPreferences {
  mode: DimensionPreferenceMode;
}

const DEFAULTS: DimensionPreferences = { mode: 'default' };

function load(): DimensionPreferences {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<DimensionPreferences>;
    return {
      mode: parsed.mode === 'custom' ? 'custom' : 'default',
    };
  } catch {
    return DEFAULTS;
  }
}

export function useDimensionPreferences() {
  const [prefs, setPrefs] = useState<DimensionPreferences>(load);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      // localStorage unavailable — ignore
    }
  }, [prefs]);

  const setMode = useCallback((mode: DimensionPreferenceMode) => {
    setPrefs((p) => ({ ...p, mode }));
  }, []);

  return { prefs, setMode };
}
